/* Хвост заказа экранов зоны (APP-SPEC-5 §1.4, §16; движок BotRun — APP-SPEC-4-circle §8): статус с этапами и счётчиком,
   итог (кадры группами, лайтбокс), вторые действия, «Что дальше», главное «Открыть в чате», чек «Списано N C · остаток M C».
   Всё — из заказа сервера: этапы (`steps`), счётчик (`progress`), группы (`result.sections`), архив (`result.archive_url`).
   Чего сервер не прислал, то не рисуется: выдуманных этапов и процентов нет. */
import { useEffect, useState, type ReactNode } from 'react';
import { endpoints } from '../../../api';
import { uiFormat, uiText } from '../../../i18n';
import { platform } from '../../../platform';
import { usePhotoJob } from '../../photo/use-photo-job';
import type { StudioOrderSnapshot } from '../../studio/studio-job-bridge';
import { record, rows, safeMediaUrl, text } from '../../studio/studio-content';
import { StudioIcon } from '../../studio/StudioIcon';
import { studioDeliveryOffText, studioDeliveryView } from '../../studio/studio-order-tail-model';
import { StudioSheet } from '../../studio/StudioSheet';
import type { useStudioForm } from '../../studio/use-studio-form';
import { useOrderImages, type OrderImage } from './use-order-images';
import './order-tail.css';

type FormState = ReturnType<typeof useStudioForm>;
export interface TailAction {
  label: string;
  icon?: string;
  onClick(images: OrderImage[]): void;
  disabled?: boolean;
}
export interface TailCopy {
  running: [string, string];
  done: [string, string];
  failed: [string, string];
  /** Подпись готового, пока бот ещё не доставил итог в чат (delivery не sent/delivered); без неё — «уже прислал» → «пришлёт» */
  donePending?: string;
}
/** «Бот уже прислал…» — только когда доставка подтверждена сервером: у заказа без доставки (not_requested, pending) это неправда. */
const pendingCopy = (sub: string) => sub.replace(/Бот уже прислал(и)?/, 'Бот пришлёт');
interface Group {
  title: string;
  images: OrderImage[];
}

/** Группы итога: секции сервера (образ, сцена), иначе одна группа. */
function groupsOf(order: Record<string, unknown>, images: OrderImage[], fallback: string): Group[] {
  const sections = rows(record(order.result).sections).filter(section => Array.isArray(section.asset_indices));
  if (!sections.length) return images.length ? [{ title: fallback, images }] : [];
  const used = new Set<number>();
  const groups = sections
    .map(section => {
      const indices = (section.asset_indices as unknown[]).filter((value): value is number => Number.isInteger(value));
      indices.forEach(index => used.add(index));
      return { title: text(section.title) || text(section.name), images: images.filter(image => indices.includes(image.index)) };
    })
    .filter(group => group.images.length);
  const rest = images.filter(image => !used.has(image.index));
  return rest.length ? [...groups, { title: fallback, images: rest }] : groups;
}

export function OrderTail({
  state,
  kind,
  copy,
  groupLabel,
  card,
  saveLabel = 'Скачать все',
  again,
  onAgain,
  next = [],
  primary,
  failLabel,
  hero,
  limit,
  children,
}: {
  state: FormState;
  kind: StudioOrderSnapshot['kind'];
  copy: TailCopy;
  /** подпись единственной группы, когда сервер не разбил итог */
  groupLabel: string;
  card?: string;
  /** null — скачивания на итоге нет («Свой аватар») */
  saveLabel?: string | null;
  /** «Ещё вариант» / «Ещё раскладка»: назад к форме с теми же настройками */
  again?: string | null;
  /** после возврата к форме по «Ещё …» (у «Коллажа» — следующая подходящая раскладка) */
  onAgain?(): void;
  next?: TailAction[];
  /** главное действие вместо «Открыть в чате» («Сделать кружок» у «Своего аватара») */
  primary?: TailAction;
  failLabel: string;
  hero?: ReactNode;
  limit?: number;
  children?: ReactNode;
}) {
  usePhotoJob(state, kind);
  const { order, orderId, busy, submissionBlocked } = state;
  const raw = record(order);
  const images = useOrderImages(order, limit);
  const [view, setView] = useState<number | null>(null);
  const [balance, setBalance] = useState<number | null>(null);
  const [hint, setHint] = useState('');
  const terminal = order && ['done', 'failed'].includes(order.status);
  useEffect(() => {
    if (!terminal) return;
    let active = true;
    endpoints
      .balance()
      .then(value => {
        if (active && typeof value.circle === 'number') setBalance(value.circle);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [terminal]);
  if (!orderId) return null;
  const status = order?.status ?? 'queued';
  const delivered = ['sent', 'delivered'].includes(text(record(raw.delivery).state));
  const [title, doneSub] = status === 'done' ? copy.done : status === 'failed' ? copy.failed : copy.running;
  // Бот сам не присылает (настройки, Q-221): подпись не обещает чат
  const deliveryView = status === 'done' ? studioDeliveryView(order) : 'pending';
  const deliveryOff = studioDeliveryOffText(deliveryView);
  const sub = deliveryOff ?? (status === 'done' && !delivered ? (copy.donePending ?? pendingCopy(doneSub)) : doneSub);
  const steps = rows(raw.steps).filter(step => text(step.title));
  const progress = record(raw.progress);
  const counted = typeof progress.done === 'number' && typeof progress.total === 'number' && progress.total > 0;
  const groups = groupsOf(raw, images.items, groupLabel);
  const flat = groups.flatMap(group => group.images);
  const archive = safeMediaUrl(record(raw.result).archive_url);
  const financial = record(order?.financial);
  const debited = Number(financial.amount_tokens ?? financial.charged_amount);
  const refunded = financial.state === 'refunded';
  const back = async () => {
    const done = await state.returnToForm();
    if (done) document.dispatchEvent(new CustomEvent('steos:studio:order-clear', { detail: { id: orderId } }));
    return !!done;
  };
  const openChat = () => {
    if (platform.kind === 'telegram') platform.close();
    else setHint(uiText(deliveryOff ?? (delivered ? 'Бот уже прислал результат в чат, в отдельную тему.' : 'Бот пришлёт результат в чат, в отдельную тему.')));
  };
  const shown = view === null ? null : flat[view];
  /* Итог из одной картинки (коллаж, один кадр) встаёт на место героя крупно, без подписи группы (§16: «коллаж, лайтбокс») */
  const single = status === 'done' && flat.length === 1 ? flat[0] : null;
  return (
    <section className="shk-tail ar" aria-live="polite">
      {hero && !single && <div className="shk-hero">{hero}</div>}
      {single && (
        <div className="shk-one">
          <button type="button" className="g" onClick={() => setView(0)}>
            <img src={single.url} alt={uiText(groups[0]?.title ?? groupLabel)} />
          </button>
        </div>
      )}
      <header className="shk-head">
        <h3>{uiText(title)}</h3>
        <p>{uiText(sub)}</p>
        {deliveryView === 'settings' && (
          <button type="button" className="ar-lnk" onClick={() => (window.location.hash = '#settings')}>
            {uiText('Настройки')}
          </button>
        )}
      </header>
      {status === 'uncertain' && <p role="status">{uiText('Результат пока не подтверждён. Проверьте этот заказ, прежде чем создавать новый.')}</p>}
      {!terminal && counted && (
        <div className="ps-prg shk-prg" role="progressbar" aria-valuemin={0} aria-valuemax={Number(progress.total)} aria-valuenow={Number(progress.done)}>
          <span className="mono">{uiFormat('{0} из {1}', Number(progress.done), Number(progress.total))}</span>
          <span className="prog">
            <b style={{ width: `${Math.min(100, (Number(progress.done) / Number(progress.total)) * 100)}%` }} />
          </span>
          {text(progress.note) && <small>{text(progress.note)}</small>}
        </div>
      )}
      {/* Этапы — пока заказ идёт и при сбое; на итоге их сменяет результат (BotRun) */}
      {!!steps.length && status !== 'done' && (
        <ol className="shk-steps">
          {steps.map(step => {
            const value = text(step.state) || text(step.status);
            return (
              <li key={text(step.key) || text(step.title)} className={value} aria-current={['running', 'active', 'now'].includes(value) ? 'step' : undefined}>
                <span className="dot" aria-hidden="true">
                  {['done', 'ok', 'completed'].includes(value) && <StudioIcon kind="check" />}
                </span>
                {text(step.title)}
              </li>
            );
          })}
        </ol>
      )}
      {!terminal && (
        <button type="button" className="btn line sm" disabled={busy || submissionBlocked} onClick={() => void state.refreshStatus()}>
          {uiText('Проверить статус заказа')}
        </button>
      )}
      {images.loading && <p role="status">{uiText('Загружаем результат…')}</p>}
      {images.error && (
        <p role="alert">
          {uiText('Не удалось загрузить результат.')}{' '}
          <button type="button" onClick={images.retry}>
            {uiText('Повторить')}
          </button>
        </p>
      )}
      {status === 'done' && !order?.result?.available && <p>{uiText('Результат недоступен. Проверьте историю заказа.')}</p>}
      {!single && groups.map(group => (
        <div className="ps-grp shk-grp" key={group.title}>
          <div className="glbl">
            {uiText(group.title)}
            <span>{group.images.length}</span>
          </div>
          <div className={group.images.length <= 4 ? 'ps-g2 shk-grid' : 'ps-g3 shk-grid'}>
            {group.images.map(image => (
              <button type="button" className="g" key={image.index} onClick={() => setView(flat.indexOf(image))}>
                <img src={image.url} alt={uiFormat('{0} · кадр {1}', uiText(group.title), group.images.indexOf(image) + 1)} />
              </button>
            ))}
          </div>
        </div>
      ))}
      {card && status === 'done' && <p className="shk-card">{card}</p>}
      {status === 'done' && (
        <>
          <div className="shk-acts">
            {saveLabel === null ? null : archive ? (
              <a className="btn line" href={archive} download>
                {uiText(saveLabel ?? '')}
              </a>
            ) : flat.length === 1 ? (
              <a className="btn line" href={flat[0].url} download={flat[0].name}>
                {uiText(saveLabel ?? '')}
              </a>
            ) : (
              !!flat.length && (
                <button
                  type="button"
                  className="btn line"
                  onClick={() =>
                    flat.forEach(image => {
                      const link = document.createElement('a');
                      link.href = image.url;
                      link.download = image.name;
                      link.click();
                    })
                  }
                >
                  {uiText(saveLabel ?? '')}
                </button>
              )
            )}
            {again !== null && (
              <button type="button" className="btn line" disabled={busy || submissionBlocked} onClick={() => void back().then(done => done && onAgain?.())}>
                {uiText(again ?? 'Ещё вариант')}
              </button>
            )}
          </div>
          {!!next.length && (
            <div className="shk-next">
              <h4>{uiText('Что дальше')}</h4>
              {next.map(action => (
                <button
                  type="button"
                  key={action.label}
                  className="ar-nx"
                  disabled={busy || action.disabled}
                  onClick={() => action.onClick(flat)}
                >
                  <StudioIcon kind={action.icon ?? 'sparkle'} />
                  <span>{uiText(action.label)}</span>
                  <StudioIcon kind="chev" />
                </button>
              ))}
            </div>
          )}
        </>
      )}
      {status === 'failed' && (
        <div className="shk-acts">
          <button type="button" className="btn" disabled={busy || submissionBlocked} onClick={() => void back()}>
            {uiText(failLabel)}
          </button>
        </div>
      )}
      {children}
      {terminal && (
        <div className="shk-foot">
          {primary && status === 'done' ? (
            <button type="button" className="btn" disabled={busy || primary.disabled} onClick={() => primary.onClick(flat)}>
              {uiText(primary.label)}
            </button>
          ) : (
            status === 'done' && (
              <button type="button" className="btn" onClick={openChat}>
                {uiText('Открыть в чате')}
              </button>
            )
          )}
          {hint && <p role="status">{hint}</p>}
          {Number.isFinite(debited) && debited > 0 && (
            <p className="shk-check">
              {uiFormat(refunded ? 'Вернули {0} C' : 'Списано {0} C', debited)}
              {balance !== null ? ` · ${uiFormat('остаток {0} C', balance)}` : ''}
            </p>
          )}
          {financial.state === 'dev_free' && <p className="shk-check">{uiText('Тестовый заказ без списания')}</p>}
        </div>
      )}
      {shown && (
        <StudioSheet title={shown.name} onClose={() => setView(null)}>
          <div className="shk-lb">
            <img src={shown.url} alt={shown.name} />
            <div className="shk-lb-nav">
              <button type="button" className="iconb" aria-label={uiText('Предыдущий')} disabled={view === 0} onClick={() => setView(value => Math.max(0, (value ?? 0) - 1))}>
                <StudioIcon kind="back" />
              </button>
              <span className="mono">{uiFormat('{0} из {1}', (view ?? 0) + 1, flat.length)}</span>
              <button
                type="button"
                className="iconb"
                aria-label={uiText('Следующий')}
                disabled={view === flat.length - 1}
                onClick={() => setView(value => Math.min(flat.length - 1, (value ?? 0) + 1))}
              >
                <StudioIcon kind="chev" />
              </button>
            </div>
            <a className="btn line" href={shown.url} download={shown.name}>
              {uiText('Скачать')}
            </a>
          </div>
        </StudioSheet>
      )}
    </section>
  );
}
